<script>
import { fromApiDatetime } from '@/util/apiDatetime';

export default {
  name: 'VoyageTable',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        current: 1,
        pageSize: 20,
        totalSize: 0,
      }),
    },
    sort: {
      type: Object,
      default: () => ({ by: 'mvygStartTime', desc: true }),
    },
  },
  computed: {
    rangeText() {
      const { current, pageSize, totalSize } = this.pagination;
      if (!totalSize) return '';
      const from = (current - 1) * pageSize + 1;
      const to = Math.min(current * pageSize, totalSize);
      return `Showing ${from}–${to} of ${totalSize}`;
    },
  },
  methods: {
    formatPeriod(value) {
      return value ? fromApiDatetime(value).format('DD MMM YYYY HH:mm') : '-';
    },
    handlePageChange(page) {
      this.$emit('page-changed', page);
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Voyage List</h5>
      <div>
        <b-button
          v-if="canCreate('voyage_master')"
          variant="primary"
          class="mr-2"
          @click="$emit('add')"
        >
          Add New Voyage
        </b-button>
        <b-button variant="outline-primary" class="mr-2" @click="$emit('compare')">
          Compare Voyages
        </b-button>
        <b-button variant="outline-secondary" @click="$emit('refresh')">
          Refresh
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center">
      <p>Loading...</p>
    </div>

    <b-table
      v-else
      striped
      hover
      responsive
      stacked="md"
      show-empty
      no-local-sorting
      no-sort-reset
      :items="items"
      :fields="fields"
      :sort-by="sort.by"
      :sort-desc="sort.desc"
      tbody-tr-class="voyage-row"
      @sort-changed="$emit('sort-changed', $event)"
      @row-clicked="$emit('view', $event)"
    >
      <template #empty>
        <div class="text-center">
          <p>No data available.</p>
        </div>
      </template>

      <template #cell(route)="{ item }">
        {{ item.mvygOriginLndm?.lndmName || '-' }}
        <i class="ri-arrow-right-line text-secondary mx-1"></i>
        {{ item.mvygDestinationLndm?.lndmName || '-' }}
      </template>

      <template #cell(mvygStartTime)="{ item }">
        <div class="text-nowrap">{{ formatPeriod(item.mvygStartTime) }}</div>
        <div class="text-nowrap text-secondary small">to {{ formatPeriod(item.mvygEndTime) }}</div>
      </template>

      <template #cell(actions)="{ item }">
        <b-button variant="primary" size="sm" class="mr-1" @click.stop="$emit('view', item)">
          Detail
        </b-button>
        <b-button
          v-if="canUpdate('voyage_master')"
          variant="warning"
          size="sm"
          class="mr-1"
          @click.stop="$emit('edit', item)"
        >
          Edit
        </b-button>
        <b-button
          v-if="canDelete('voyage_master')"
          size="sm"
          variant="outline-danger"
          @click.stop="$emit('delete', item)"
        >
          Delete
        </b-button>
      </template>
    </b-table>

    <div v-if="items && items.length > 0" class="d-flex justify-content-between align-items-center flex-wrap">
      <span class="text-secondary small">{{ rangeText }}</span>
      <b-pagination
        v-model="pagination.current"
        :per-page="pagination.pageSize"
        :total-rows="pagination.totalSize"
        class="mb-0"
        @change="handlePageChange"
      ></b-pagination>
    </div>
  </div>
</template>

<style scoped>
/* baris bisa diklik ke detail */
:deep(.voyage-row) {
  cursor: pointer;
}

/* kartu di HP */
@media (max-width: 767.98px) {
  :deep(table.b-table-stacked-md) {
    display: block;
    width: 100%;
    overflow-x: visible;
  }

  :deep(table.b-table-stacked-md > thead),
  :deep(table.b-table-stacked-md > tfoot) {
    display: none;
  }

  :deep(table.b-table-stacked-md > tbody),
  :deep(table.b-table-stacked-md > tbody > tr),
  :deep(table.b-table-stacked-md > tbody > tr > td),
  :deep(table.b-table-stacked-md > tbody > tr > th) {
    display: block;
  }

  :deep(table.b-table-stacked-md > tbody > tr) {
    padding: 0.5rem 0;
    border-bottom: 1px solid #e9ecef;
  }

  :deep(table.b-table-stacked-md > tbody > tr > [data-label]) {
    padding: 0.25rem 0.5rem;
    border: 0;
  }

  :deep(table.b-table-stacked-md > tbody > tr > [data-label]::before) {
    content: attr(data-label);
    width: 40%;
    float: left;
    text-align: left;
    font-weight: 600;
    color: #6c757d;
    padding-right: 0.5rem;
  }

  :deep(table.b-table-stacked-md > tbody > tr > [data-label]::after) {
    display: block;
    clear: both;
    content: "";
  }

  :deep(table.b-table-stacked-md > tbody > tr > [data-label] > div) {
    display: inline-block;
    width: 60%;
  }

  /* tombol aksi sebaris penuh */
  :deep(table.b-table-stacked-md > tbody > tr > [data-label=""]::before) {
    display: none;
  }

  :deep(table.b-table-stacked-md > tbody > tr > [data-label=""] > div) {
    width: 100%;
  }
}
</style>
